import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import ReactMarkdown from "react-markdown";
import removeMd from "remove-markdown";
import { animateScroll } from "react-scroll";

import {
    selectEraContent,
    selectLocations,
} from "./../../selectors.js";
import * as actions from "./../../actions/index";
import { ApiConfig, API_URL } from "./../../settings";

import "./style.css";
import BigSpacer from "./../../components/BigSpacer";
import MediumSpacer from "./../../components/MediumSpacer";
import LoadingBox from "./../../components/LoadingBox";
import SlideShow from "./../../components/SlideShow";
import ChurchFacts from "./../../components/ChurchFacts";
import ChurchVisitingHours from "./../../components/ChurchVisitingHours";
import ChurchAccessibility from "./../../components/ChurchAccessibility";
import EraBox from "./../../components/EraBox";
import MapWrapper from "./../../containers/MapWrapper";
import ShowCaseBox from "./../../components/ShowCaseBox";
import HistoryPreviewBox from "./../../components/HistoryPreviewBox";
import ShareButtons from "./../../components/ShareButtons";
import ChurchMardown from "../../components/ChurchMarkdown/index.js";

import ChurchPrice from "../../components/Price/index.js";
import { updateCanonicalLink } from "../../helpers/helpers.js";

class Church extends Component {
    constructor() {
        super();

        this.state = {
            church: null,
            content: null,
            era: null,
            album: null,
            stories: null,
            activeEra: "1000",
            albumError: false,
            contentError: false,
            churchError: false,
        };

        this.findCurrentEra = this.findCurrentEra.bind(this);
    }

    componentDidMount() {
        this._ismounted = true;

        this.getChurchesFacts(this.props.match.params.id);
        updateCanonicalLink();
        animateScroll.scrollToTop({ duration: 300 });
    }

    componentWillUnmount() {
        this._ismounted = false;
    }

    componentDidUpdate(prevProps) {
        const prevEra = prevProps.era;
        const thisEra = this.props.era;

        const prevParams = prevProps.location.pathname;
        const thisParams = this.props.location.pathname;

        const { church } = this.state;

        if (church) {
            this.updatePageMeta(church);
        }

        if (prevParams !== thisParams && thisParams) {
            this.getChurchesFacts(this.props.match.params.id);
            animateScroll.scrollToTop({ duration: 300 });
        }

        if (
            prevEra !== thisEra &&
            thisEra !== null &&
            this.state.content !== null
        ) {
            this.findCurrentEra();
        }
    }

    findCurrentEra() {
        const { era } = this.props;
        const { content } = this.state;

        if (!era || !content) return;

        const currentEra = era.find(
            (item) => item.sys.id === content.era.sys.id
        );
        this.setState({ era: currentEra.fields });
    }

    getAlbum(historicalBuildingCode) {
        // NOTE: For now, we use historicalBuildingCode to get images from Cloudinary.
        const folderId = historicalBuildingCode ? historicalBuildingCode : "";

        axios
            .get(
                `https://res.cloudinary.com/kirkesok/image/list/${folderId}.json`
            )
            .then((res) => {
                if (!this._ismounted) return;

                this.checkIfAlbumHasMainImage(res.data.resources);
                this.setState({
                    album: res.data.resources,
                    albumError: false,
                });
            })
            .catch((err) => {
                console.error("Error fetching album", err);
                this.setState({ albumError: true });
            });
    }

    checkIfAlbumHasMainImage(album) {
        const mainImageIndex = album.findIndex(
            (item) => item.context.custom["PubInfo.Kategori"] === "Hovedbilde"
        );
        if (mainImageIndex === -1) return;

        // Some magic I found online
        // https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
        album.splice(0, 0, album.splice(mainImageIndex, 1)[0]);
    }

    getChurchesFacts(id) {
        this.setState({ loading: true });

        axios
            .get(`${API_URL}/api/churches/id/${id}`, ApiConfig)
            .then((res) => {
                if (!this._ismounted) return;
                this.props.setLocation(res.data.coordinates);
                this.setState({
                    activeEra: res.data.constructionYear,
                    church: res.data,
                    churchError: false,
                });

                this.getAlbum(res.data.historicalBuildingCode);
                this.getChurchContent(res.data.historicalBuildingCode);
            })
            .catch((err) => {
                console.error("Error ved henting av en enkelt kirke", err);
                this.setState({
                    churchError: true,
                });
            });
    }

    getChurchContent(id) {
        axios
            .get(`${API_URL}/api/churches/id/${id}/additional-info`, ApiConfig)
            .then((response) => {
                if (!this._ismounted) return;
                this.setState({
                    content: response.data.fields,
                    contentError: false,
                });
                this.getConnectedStory(id);
                if (this.props.era && !this.state.era) this.findCurrentEra();
            })
            .catch((error) => {
                console.error(error);
                this.setState({ contentError: true });
            })
    }

    getConnectedStory(id) {
        axios
            .get(`${API_URL}/api/stories/churches/${id}`, ApiConfig)
            .then((response) => {
                if (!this._ismounted) return;
                this.setState({ stories: response.data });
            })
            .catch((error) => {
                console.error("Klarte ikke koble historie med kirke", error);
            })
    }

    printConnectedStories() {
        const { stories } = this.state;

        return stories.map((data) => {
            const content = removeMd(data.fields.body);
            const excerptArray = content.split(".").slice(0, 2); // Henter et utdrag
            const excerpt = excerptArray.join(". ");

            const img =
                data.fields.hasOwnProperty("featuredImage") &&
                data.fields.featuredImage;
            const imageUrl = img
                ? `${img.substring(
                    0,
                    img.indexOf("?") !== -1 ? img.indexOf("?") : img.length
                )}?w=400`
                : "https://placehold.co/600x400";

            if (stories.length > 1) {
                return (
                    <HistoryPreviewBox
                        key={data.sys.id}
                        title={data.fields.title}
                        link={`/historier/${data.sys.id}`}
                        description={`${excerpt}...`}
                        image={imageUrl}
                    />
                );
            } else {
                return (
                    <ShowCaseBox
                        image={imageUrl}
                        title={data.fields.title}
                        text={`${excerpt}...`}
                        key={data.sys.id}
                        to={`/historier/${data.sys.id}`}
                    />
                );
            }
        });
    }

    updatePageMeta(church) {
        // church description from state is actually a title
        if (church.description){
            document.title = church.description;
            let titleOgTag = document.querySelector("meta[property='og:title']");
            titleOgTag.setAttribute('content', church.description);
        }

        let descTag = document.querySelector("meta[name='description']");
        let descOgTag = document.querySelector("meta[property='og:description']");
        if (church.additionalText){
            descTag.setAttribute('content', church.additionalText);
            descOgTag.setAttribute('content', church.additionalText) 
        } else if (church.description) {
            descTag.setAttribute('content', church.description);
            descOgTag.setAttribute('content', church.description)
        }
  
    }

    render() {
        const {
            church,
            album,
            content,
            era,
            stories,
            activeEra,
            albumError,
            // contentError,
            churchError,
        } = this.state;
        
        return (
            <div className="church">
                <MediumSpacer />
                <div className="container">
                    <button
                        className="back-button"
                        onClick={() => this.props.history.push("/sok")}
                    >
                        <i className="arrow-left"></i> Tilbake til søk
                    </button>
                </div>
                <MediumSpacer />

                <div className="container">
                    <h1 className="page-title">
                        {church && church.description}
                    </h1>
                    <div className="church-section-1">
                        <div className="church-ingress">
                            <div>
                                {/* {contentError && (
                                    <p>
                                        Det oppstod en feil ved henting av
                                        innhold. Prøv å laste siden på nytt.
                                    </p>
                                )} */}

                                {churchError ? null : church ? (
                                    <ChurchMardown church={church} />
                                ) : (
                                    <LoadingBox text="Laster Ingress" />
                                )}
                                {content &&
                                    era &&
                                    content.hasOwnProperty("extraIngress") && (
                                        <ReactMarkdown
                                            source={content.extraIngress}
                                        />
                                    )}
                                {/* {church && church.description && (
                                    <p>
                                        <strong>{church.description}</strong>
                                    </p>
                                )} */}
                            </div>
                        </div>
                        {/* Slideshow */}
                        <div className="church-slider-wrapper">
                            {!albumError && (
                                <div className="church-slider-inner">
                                    {album ? (
                                        <SlideShow images={this.state.album} />
                                    ) : (
                                        <LoadingBox text="Laster bildegalleri" />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* END section 1 */}

                    <MediumSpacer />

                    <ShareButtons
                        image={this.state.album && this.state.album[0]}
                    />

                    <MediumSpacer />

                    <div className="church-section-4">
                        {churchError ? null : church ? (
                            <ChurchFacts church={church} />
                        ) : (
                            <LoadingBox text="Laster fakta" />
                        )}
                        {churchError ? null : church ? (
                            <ChurchVisitingHours church={church} />
                        ) : (
                            <LoadingBox text="Laster apen kirke" />
                        )}
                        {churchError ? null : church ? (
                            <ChurchAccessibility church={church} />
                        ) : (
                            <LoadingBox text="Laster tilgjengelighet" />
                        )}
                        {churchError ? null : church ? (
                            <ChurchPrice church={church} />
                        ) : (
                            <LoadingBox text="Laster pris" />
                        )}
                    </div>
                    {/* END section 4 */}
                </div>
                {/* END container */}

                <MediumSpacer />

                <div className="container reading">
                    {content && era && <ReactMarkdown source={content.body} />}
                </div>

                <MediumSpacer />
                <MapWrapper single />

                <MediumSpacer />

                {/* CONNECTED SHORT STORIES */}
                {stories && stories.length === 1 && (
                    <div className="container">
                        {this.printConnectedStories()}
                    </div>
                )}
                {stories && stories.length > 1 && (
                    <div className="container">
                        <div className="grid-column-3">
                            {this.printConnectedStories()}
                        </div>
                    </div>
                )}
                {stories && <MediumSpacer />}

                {/* ERAS / EPOKER */}
                <div className="container">
                    {era && <EraBox activeEra={activeEra} />}
                </div>

                <BigSpacer />
            </div>
        );
    }
}
const mapStateToProps = createStructuredSelector({
    era: selectEraContent(),
    locations: selectLocations(),
});

const mapDispatchToProps = (dispatch) => {
    return {
        setLocation: (data) => dispatch(actions.setLocation(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Church);



// WEBPACK FOOTER //
// ./src/containers/Church/index.js